/**
 * StatusCards
 *
 * Triple-card row at the bottom of the dashboard: Active Tests · This Week · Plan.
 * All three share the canonical card pattern (border-b header + pt-4 content) for
 * visual consistency with the rest of the app.
 *
 * Renamed from "In Flight" — that copy was unclear to users. "Active Tests"
 * names the thing directly.
 *
 * Cards are data-driven: when real signal exists (experiments, views, plan
 * details) they render it; otherwise they fall back to a single-row empty
 * state with the next-action CTA.
 */

import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { McpClients, McpConnectButton, McpEyebrow, McpHeadline, McpTryAsking } from '@/components/shared/mcp/McpIntro'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { HugeiconsIcon } from '@hugeicons/react'
import type { IconSvgElement } from '@hugeicons/react'
import {
  TestTube01Icon,
  Calendar03Icon,
  AnalyticsUpIcon,
  Chart01Icon,
  Loading01Icon,
  Tick02Icon,
} from '@hugeicons/core-free-icons'
import type { Experiment } from '@/hooks/useExperiments'
import { getPlanById, getPlanDisplayName } from '@/lib/constants/pricing'
import type { PlanTier } from '@/lib/types/billing'

const UNLIMITED_LIMIT = 999999999

/** Compact plan-detail bullets sourced from `PRICING_PLANS` (same as humblytics.com/pricing). */
function getPlanDetailBullets(
  planTier: string,
  opts: { hideUsageOverlaps: boolean },
): string[] {
  const plan = getPlanById(planTier as PlanTier)
  const raw =
    plan?.features ??
    ({
      free: [
        'Basic analytics on 1 site',
        'Up to 1K events / month',
        'Pages · devices · channels · referrers',
      ],
      plus_legacy: [
        '1 A/B test (grandfathered)',
        'Basic analytics',
        'API access',
      ],
    } as Record<string, string[]>)[planTier] ??
    []

  const filtered = opts.hideUsageOverlaps
    ? raw.filter((f) => {
        const lower = f.toLowerCase()
        // Usage block already covers datapoints / sites / seats
        return (
          !lower.includes('datapoint') &&
          !lower.includes('event') &&
          !lower.includes('website') &&
          !lower.includes('team member')
        )
      })
    : raw

  // Prefer differentiators users look up (tests / AI / connectors) over base tracking
  const PRIORITY = [
    'a/b',
    'funnel',
    'heatmap',
    'ai insight',
    'priority',
    'connector',
    'attribution',
    'api',
    'white label',
    'slack',
  ]
  const ranked = [...filtered].sort((a, b) => {
    const score = (f: string) => {
      const lower = f.toLowerCase()
      const idx = PRIORITY.findIndex((k) => lower.includes(k))
      return idx === -1 ? 99 : idx
    }
    return score(a) - score(b)
  })

  // Keep the card compact beside the MCP surface — top differentiators only
  return ranked.slice(0, 5)
}

// ── Generic Card frame ─────────────────────────────────────────────────────────
//
// Plain monochrome icons + uppercase eyebrow + big-stat headline + right-aligned
// action. No tinted icon chips, no border-b header divider — typography does
// the separation. Tight p-4 padding for utilitarian density.

interface CardFrameProps {
  title: string
  icon: IconSvgElement
  /** Optional headline number — rendered large beneath the title. */
  headline?: React.ReactNode
  /** Inline detail text following the headline number. */
  headlineDetail?: React.ReactNode
  children: React.ReactNode
  action?: { label: string; href: string }
}

function StatusCard({
  title,
  icon,
  headline,
  headlineDetail,
  children,
  action,
}: CardFrameProps) {
  const navigate = useNavigate()
  return (
    <Card className="border-border/30 flex flex-col">
      <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-2">
        <div className="flex items-start gap-2.5 min-w-0">
          <HugeiconsIcon icon={icon} size={14} className="text-muted-foreground shrink-0 mt-px" />
          <div className="min-w-0">
            <div className="text-xs font-semibold text-muted-foreground leading-none mb-1.5">
              {title}
            </div>
            {headline != null && (
              <div className="flex items-baseline gap-2 leading-none">
                <span className="text-[22px] font-semibold tabular-nums text-foreground tracking-tight">
                  {headline}
                </span>
                {headlineDetail != null && (
                  <span className="text-xs text-muted-foreground leading-none">
                    {headlineDetail}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {action && (
          <button
            type="button"
            onClick={() => navigate(action.href)}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            {action.label} →
          </button>
        )}
      </div>
      <div className="px-4 pb-4 pt-1 flex-1 space-y-2">{children}</div>
    </Card>
  )
}

// ── Single-line empty row (faint, in-card) ─────────────────────────────────────

function EmptyRow({
  children,
  action,
}: {
  children: React.ReactNode
  action?: { label: string; href: string }
}) {
  const navigate = useNavigate()
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground/80 py-1">
      <span className="size-1 rounded-full bg-muted-foreground/40 shrink-0" aria-hidden />
      <span>{children}</span>
      {action && (
        <>
          <span className="text-muted-foreground/40">·</span>
          <button
            type="button"
            onClick={() => navigate(action.href)}
            className="text-foreground/80 hover:text-foreground transition-colors"
          >
            {action.label} →
          </button>
        </>
      )}
    </div>
  )
}

// ── Active Tests (was In Flight) ───────────────────────────────────────────────

const STATUS_TONE: Record<Experiment['status'], string> = {
  active: 'bg-success/15 text-success dark:text-success',
  paused: 'bg-warning/15 text-warning dark:text-warning',
  completed: 'bg-muted text-muted-foreground',
  draft: 'bg-muted text-muted-foreground',
  archived: 'bg-muted text-muted-foreground',
}

const STATUS_LABEL: Record<Experiment['status'], string> = {
  active: 'Running',
  paused: 'Paused',
  completed: 'Completed',
  draft: 'Draft',
  archived: 'Archived',
}

interface ActiveTestsProps {
  basePath: string
  experiments?: Experiment[]
  savedFunnels?: Array<{ id?: string; name?: string } | unknown>
  isLoading?: boolean
  /** Pre-computed count of tests that have reached statistical significance. */
  readyToCallCount?: number
}

function ActiveTestsCard({
  basePath,
  experiments = [],
  savedFunnels = [],
  isLoading = false,
  readyToCallCount = 0,
}: ActiveTestsProps) {
  const navigate = useNavigate()
  const activeTests = experiments.filter((e) => e.status === 'active')
  const pausedTests = experiments.filter((e) => e.status === 'paused')
  const totalActiveOrPaused = activeTests.length + pausedTests.length
  const funnelCount = Array.isArray(savedFunnels) ? savedFunnels.length : 0
  const hasContent = totalActiveOrPaused > 0 || funnelCount > 0

  // Two-column internal layout: header zone (left, 1fr) + tests list (right,
  // 1.6fr). The card spans the full page width now that This Week / Plan are
  // gone, so we use that horizontal space instead of stacking everything in a
  // narrow column.
  const inlineTests = [...activeTests, ...pausedTests].slice(0, 4)
  const remaining = totalActiveOrPaused - inlineTests.length

  return (
    <Card className="border-border/30 overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] divide-y md:divide-y-0 md:divide-x divide-border/40">
        {/* Header zone — eyebrow + big stat + meta */}
        <div className="p-4 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2.5 min-w-0">
              <HugeiconsIcon
                icon={TestTube01Icon}
                size={14}
                className="text-muted-foreground shrink-0 mt-px"
              />
              <div className="text-xs font-semibold text-muted-foreground leading-none mt-px">
                Active Tests
              </div>
            </div>
            {hasContent && (
              <button
                type="button"
                onClick={() => navigate(`${basePath}/split-testing`)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                View all →
              </button>
            )}
          </div>

          {hasContent ? (
            <>
              <div className="flex items-baseline gap-2 leading-none">
                <span className="text-[28px] font-semibold tabular-nums text-foreground tracking-tight leading-none">
                  {activeTests.length}
                </span>
                <span className="text-xs text-muted-foreground leading-none">
                  running
                </span>
              </div>
              {readyToCallCount > 0 && (
                <div className="text-xs text-success dark:text-success font-medium">
                  {readyToCallCount} ready to call
                </div>
              )}
              {funnelCount > 0 && (
                <button
                  type="button"
                  onClick={() => navigate(`${basePath}/funnel-analyzer`)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors text-left inline-flex items-center gap-1.5 mt-auto"
                >
                  <HugeiconsIcon icon={Chart01Icon} size={11} />
                  <span className="tabular-nums text-foreground font-medium">{funnelCount}</span>
                  saved funnel{funnelCount === 1 ? '' : 's'} →
                </button>
              )}
            </>
          ) : (
            <div className="text-xs text-muted-foreground leading-relaxed">
              No experiments running yet.
            </div>
          )}
        </div>

        {/* Tests list */}
        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
              <HugeiconsIcon icon={Loading01Icon} size={12} className="animate-spin" />
              Loading tests…
            </div>
          ) : !hasContent ? (
            <div className="flex flex-col gap-2">
              <EmptyRow action={{ label: 'Create one', href: `${basePath}/split-testing/new` }}>
                No tests running yet
              </EmptyRow>
              <EmptyRow action={{ label: 'Build one', href: `${basePath}/funnel-analyzer` }}>
                No saved funnels yet
              </EmptyRow>
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-border/30">
              {inlineTests.map((test) => {
                const variantCount = test.variants?.length ?? 0
                const sessions = test.totalSessions ?? 0
                return (
                  <button
                    key={test.experimentId}
                    type="button"
                    onClick={() =>
                      navigate(`${basePath}/split-testing/${test.experimentId}`)
                    }
                    className="w-full text-left group py-2 -mx-2 px-2 rounded-md hover:bg-muted/40 transition-colors first:pt-0 last:pb-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-foreground truncate">
                        {test.experimentName || 'Untitled test'}
                      </span>
                      <span
                        className={`text-xs font-medium px-1.5 py-0.5 rounded-full shrink-0 leading-none ${
                          STATUS_TONE[test.status] ?? STATUS_TONE.draft
                        }`}
                      >
                        {STATUS_LABEL[test.status] ?? test.status}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 tabular-nums">
                      {variantCount} variant{variantCount === 1 ? '' : 's'}
                      {sessions > 0 && (
                        <>
                          {' · '}
                          <span>{sessions.toLocaleString()}</span> sessions
                        </>
                      )}
                    </div>
                  </button>
                )
              })}
              {remaining > 0 && (
                <div className="text-xs text-muted-foreground pt-2 first:pt-0">
                  +{remaining} more test{remaining === 1 ? '' : 's'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

// Back-compat alias — older imports of InFlightCard keep working until they're
// migrated. New code should use ActiveTestsCard directly.
const InFlightCard = ActiveTestsCard

// ── This Week ──────────────────────────────────────────────────────────────────

interface ThisWeekProps {
  basePath: string
  viewsToday?: number
  eventsToday?: number
  /** Optional: views over the last 7d to compute delta. If unknown, we just show today's number. */
  viewsThisWeek?: number
  viewsLastWeek?: number
  /** Highest-traffic site name (for multi-site users). */
  topSite?: { name: string; views: number }
}

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`
  if (n >= 10_000) return `${Math.round(n / 1_000)}K`
  if (n >= 1_000) return `${+(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

function ThisWeekCard({
  basePath,
  viewsToday = 0,
  eventsToday = 0,
  viewsThisWeek,
  viewsLastWeek,
  topSite,
}: ThisWeekProps) {
  const hasAnyData = viewsToday > 0 || eventsToday > 0 || (viewsThisWeek ?? 0) > 0

  // Week-over-week delta when both values are present and meaningful.
  const weekDelta =
    viewsThisWeek != null && viewsLastWeek != null && viewsLastWeek > 0
      ? Math.round(((viewsThisWeek - viewsLastWeek) / viewsLastWeek) * 100)
      : null

  // Subtitle leads with the number — "7,956 views today" — instead of the
  // category. Carries data, not a label.
  const subtitle = hasAnyData
    ? `${formatNum(viewsToday)} views today${
        weekDelta != null && weekDelta !== 0
          ? ` · ${weekDelta > 0 ? '+' : ''}${weekDelta}% WoW`
          : ''
      }`
    : 'Traffic activity'

  return (
    <StatusCard
      title="This Week"
      icon={Calendar03Icon}
      action={
        hasAnyData ? { label: 'Analytics', href: `${basePath}/traffic` } : undefined
      }
    >
      {!hasAnyData ? (
        <EmptyRow action={{ label: 'View analytics', href: `${basePath}/traffic` }}>
          Not enough data yet
        </EmptyRow>
      ) : (
        <>
          {/* Views today — always render when we have any signal. The
              dashboard's status ribbon shows the same number above but this
              card is the durable home for it. */}
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-xs text-muted-foreground">Views today</span>
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {formatNum(viewsToday)}
            </span>
          </div>

          {viewsThisWeek != null && viewsThisWeek > 0 && (
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs text-muted-foreground">Last 7 days</span>
              <span className="text-sm font-semibold tabular-nums text-foreground inline-flex items-center gap-1.5">
                {formatNum(viewsThisWeek)}
                {weekDelta != null && weekDelta !== 0 && (
                  <span
                    className={`text-xs font-medium tabular-nums ${
                      weekDelta > 0
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {weekDelta > 0 ? '↗' : '↘'}{' '}
                    {weekDelta > 0 ? '+' : ''}
                    {weekDelta}%
                  </span>
                )}
              </span>
            </div>
          )}

          {eventsToday > 0 && (
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs text-muted-foreground">Events today</span>
              <span className="text-sm font-semibold tabular-nums text-foreground">
                {formatNum(eventsToday)}
              </span>
            </div>
          )}

          {topSite && topSite.views > 0 && (
            <div className="flex items-start gap-2 pt-2 border-t border-border/30 text-xs">
              <HugeiconsIcon
                icon={AnalyticsUpIcon}
                size={12}
                className="text-success mt-0.5 shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs text-muted-foreground">Most active site</div>
                <div className="text-foreground font-medium truncate">{topSite.name}</div>
              </div>
              <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                {formatNum(topSite.views)}
              </span>
            </div>
          )}
        </>
      )}
    </StatusCard>
  )
}

// ── Plan ───────────────────────────────────────────────────────────────────────

interface PlanProps {
  planName: string
  planTier: 'free' | 'plus' | 'plus_legacy' | 'business' | 'scale' | string
  isFreePlan: boolean
  hasActiveTrial: boolean
  /** Stripe subscription still in its trial window (Business/Scale checkout trial). */
  isStripeTrialing?: boolean
  hasActiveSubscription: boolean
  usedTrial: boolean
  trialDaysLeft?: number
  trialEndsAt?: Date | string | null
  /** Monthly price of the current plan ($/mo). */
  monthlyPrice?: number
  isAnnualBilling?: boolean
  /** Stripe status (`past_due` renders the payment-failed card). */
  subscriptionStatus?: string | null
  /** Canceled paid plan: history stays readable, creation is paywalled. */
  isReadOnly?: boolean
  /** Datapoints (events) usage for the current period. */
  datapointsUsed?: number
  datapointsLimit?: number
  /** Sites in use vs. plan's site limit. */
  sitesUsed?: number
  sitesLimit?: number
  /** Saved funnels and heatmap pages, shown on trial cards ("Funnels 4 · Heatmaps 2"). */
  funnelsUsed?: number
  heatmapsUsed?: number
  /**
   * Payment problem line for a past-due card ("Card ending 4242 declined Sep 24").
   * `/billing/status` doesn't carry the card or decline date yet, so the card
   * falls back to a generic line when this is omitted.
   */
  paymentIssueLabel?: string
  /** Team seat usage — only surfaced when the plan supports team access. */
  seatsUsed?: number
  seatsLimit?: number
  hasTeamAccess?: boolean
  onUpgrade: () => void
  onStartTrial: () => void
  onManageBilling: () => void
  onCancelPlan?: () => void
  /** Subscription data is still loading — render skeleton instead of flashing defaults. */
  isLoading?: boolean
  /** Trial sign-up is in flight (Stripe redirect returned, webhook pending). */
  isActivating?: boolean
}

function formatShortDay(d?: Date | string | null): string | null {
  if (!d) return null
  const date = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

type PlanCardState =
  | 'paid'
  | 'past-due'
  | 'read-only'
  | 'trial-active'
  | 'stripe-trial'
  | 'trial-ended'
  | 'trial-available'
  | 'free'

/** Exported for tests: which card the hub shows for this billing state. */
export function resolvePlanCardState({
  planTier,
  isFreePlan,
  hasActiveTrial,
  isStripeTrialing = false,
  hasActiveSubscription,
  usedTrial,
  subscriptionStatus,
  isReadOnly = false,
}: Pick<
  PlanProps,
  | 'planTier'
  | 'isFreePlan'
  | 'hasActiveTrial'
  | 'isStripeTrialing'
  | 'hasActiveSubscription'
  | 'usedTrial'
  | 'subscriptionStatus'
  | 'isReadOnly'
>): PlanCardState {
  // Defensive: if planTier indicates a paid tier but hasActiveSubscription is
  // false, trust the planTier. Dev / staging envs sometimes have planName +
  // planTier set without the subscription flag being wired.
  const looksPaidFromTier = !!planTier && planTier !== 'free' && planTier !== ''
  if (isReadOnly) return 'read-only'
  if (hasActiveTrial) return 'trial-active'
  if (isStripeTrialing && looksPaidFromTier) return 'stripe-trial'
  if (subscriptionStatus?.toLowerCase() === 'past_due') return 'past-due'
  if (hasActiveSubscription || looksPaidFromTier) return 'paid'
  if (isFreePlan && !usedTrial) return 'trial-available'
  if (usedTrial) return 'trial-ended'
  return 'free'
}

export function PlanCard({
  planName,
  planTier,
  isFreePlan,
  hasActiveTrial,
  isStripeTrialing = false,
  hasActiveSubscription,
  usedTrial,
  trialEndsAt,
  monthlyPrice,
  isAnnualBilling = false,
  subscriptionStatus,
  isReadOnly = false,
  datapointsUsed = 0,
  datapointsLimit = 0,
  sitesUsed = 0,
  sitesLimit = 0,
  funnelsUsed,
  heatmapsUsed,
  paymentIssueLabel,
  seatsUsed = 0,
  seatsLimit = 0,
  hasTeamAccess = false,
  onUpgrade,
  onStartTrial,
  onManageBilling,
  onCancelPlan,
  isLoading = false,
  isActivating = false,
}: PlanProps) {
  const state = resolvePlanCardState({
    planTier,
    isFreePlan,
    hasActiveTrial,
    isStripeTrialing,
    hasActiveSubscription,
    usedTrial,
    subscriptionStatus,
    isReadOnly,
  })

  const isScale = planTier === 'scale'
  const isTrialLike = state === 'trial-active' || state === 'stripe-trial'
  const isTrialFamily = isTrialLike || state === 'trial-ended'
  const isPaidFamily = state === 'paid' || state === 'past-due' || state === 'read-only'

  // Trials are Business (or Scale for a Scale checkout trial). Plus is never named.
  const trialPlan = getPlanById(isScale ? 'scale' : 'business')
  const displayPlanName = isTrialFamily
    ? trialPlan?.name ?? 'Business'
    : getPlanDisplayName(planTier, planName)
  const trialEndLabel = formatShortDay(trialEndsAt)

  // Usage progress — clamp + handle "unlimited" sentinel.
  const datapointsPct =
    datapointsLimit > 0 && datapointsLimit < UNLIMITED_LIMIT
      ? Math.min(100, Math.round((datapointsUsed / datapointsLimit) * 100))
      : 0
  const hasUnlimitedSites = sitesLimit >= UNLIMITED_LIMIT
  const hasUnlimitedDatapoints = datapointsLimit >= UNLIMITED_LIMIT

  // Compact hub layout: usage for paid/trial; feature list only when pitching upgrade.
  const showsUsage =
    (isPaidFamily || isTrialFamily) &&
    (datapointsLimit > 0 || sitesLimit > 0 || (hasTeamAccess && seatsLimit > 0))
  const features = getPlanDetailBullets(planTier, {
    hideUsageOverlaps: showsUsage,
  }).slice(0, 3)
  const showIncludes = features.length > 0 && (state === 'free' || state === 'trial-available')

  if (isLoading) {
    return (
      <Card className={ACCOUNT_CARD}>
        <div className="flex flex-col gap-3.5 px-5 py-[18px]">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </Card>
    )
  }

  if (isActivating) {
    return (
      <Card className={ACCOUNT_CARD}>
        <div className="flex flex-col gap-3.5 px-5 py-[18px]">
          <div className="text-xs font-medium leading-4 text-muted-foreground">Current plan</div>
          <div className="flex items-center gap-2">
            <HugeiconsIcon icon={Loading01Icon} size={14} className="shrink-0 animate-spin text-muted-foreground" />
            <span className="text-sm font-medium leading-5 text-foreground">Activating your trial…</span>
          </div>
        </div>
      </Card>
    )
  }

  const statusPill =
    state === 'paid' ? (
      <StatusPill live>Active</StatusPill>
    ) : state === 'past-due' ? (
      <StatusPill tone="warning">Past due</StatusPill>
    ) : state === 'read-only' ? (
      <StatusPill>Read-only</StatusPill>
    ) : isTrialLike ? (
      <StatusPill live>Trial</StatusPill>
    ) : state === 'trial-ended' ? (
      <StatusPill tone="rust">Trial ended</StatusPill>
    ) : null

  // Top-right text action. Past due opens the portal, where the open invoice lives.
  const headerAction =
    state === 'past-due'
      ? { label: 'Open invoice', onClick: onManageBilling }
      : state === 'paid' || state === 'stripe-trial'
        ? { label: state === 'stripe-trial' ? 'Manage trial' : 'Manage billing', onClick: onManageBilling }
        : isTrialFamily
          ? { label: 'Manage trial', onClick: onUpgrade }
          : state === 'read-only'
            ? { label: 'Resubscribe', onClick: onUpgrade }
            : null

  const priceLine =
    isTrialLike && trialPlan
      ? trialEndLabel
        ? `free until ${trialEndLabel}, then $${trialPlan.monthlyPrice}/mo`
        : `free trial, then $${trialPlan.monthlyPrice}/mo`
      : state === 'trial-ended'
        ? trialEndLabel
          ? `trial ended ${trialEndLabel}`
          : 'trial ended'
        : state === 'read-only'
          ? 'plan ended'
          : (state === 'paid' || state === 'past-due') && monthlyPrice != null
            ? `$${monthlyPrice}/mo · renews ${isAnnualBilling ? 'yearly' : 'monthly'}`
            : null

  const ctaLabel =
    state === 'trial-available'
      ? 'Start 14-day Business trial'
      : state === 'free'
        ? 'Upgrade to Business'
        : null
  const ctaAction = state === 'trial-available' ? onStartTrial : onUpgrade
  const showsPlanDetails = isPaidFamily || isTrialFamily
  const counts =
    funnelsUsed != null || heatmapsUsed != null
      ? [
          funnelsUsed != null ? `Funnels ${funnelsUsed}` : null,
          heatmapsUsed != null ? `Heatmaps ${heatmapsUsed}` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null

  return (
    <Card className={ACCOUNT_CARD}>
      <div className="flex h-full flex-col gap-3.5 px-5 py-[18px]">
        {/* Label + status + billing */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium leading-4 text-muted-foreground">Current plan</span>
          {statusPill}
          <div className="flex-1" />
          {headerAction && (
            <button
              type="button"
              onClick={headerAction.onClick}
              className="shrink-0 text-xs font-medium leading-4 text-foreground transition-colors hover:text-muted-foreground"
            >
              {headerAction.label}
            </button>
          )}
          {onCancelPlan && state === 'paid' && (
            <button
              type="button"
              onClick={onCancelPlan}
              className="shrink-0 text-xs leading-4 text-muted-foreground transition-colors hover:text-foreground"
            >
              Cancel plan
            </button>
          )}
        </div>

        {/* Plan name + price */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-[22px] font-semibold leading-[26px] tracking-[-0.02em] text-foreground">
            {state === 'trial-available' ? 'Free' : displayPlanName}
          </span>
          {priceLine && (
            <span className="text-[13px] leading-4 text-muted-foreground tabular-nums">{priceLine}</span>
          )}
        </div>

        {/* Usage meter */}
        {showsUsage && datapointsLimit > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex justify-between gap-3">
              <span className="text-xs leading-4 text-muted-foreground">Data points this month</span>
              <span className="text-xs font-medium leading-4 text-foreground tabular-nums">
                {state === 'trial-ended'
                  ? `Paused at ${formatNum(datapointsUsed)}`
                  : `${formatNum(datapointsUsed)} of ${hasUnlimitedDatapoints ? 'unlimited' : formatNum(datapointsLimit)}`}
              </span>
            </div>
            {!hasUnlimitedDatapoints && (
              <div className="flex h-1.5 shrink-0 overflow-hidden rounded-full bg-muted">
                <div
                  className="rounded-full bg-foreground transition-[width] duration-500"
                  style={{ width: `${datapointsPct}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Includes — free / trial upgrade pitch only (max 3) */}
        {showIncludes && (
          <ul className="flex flex-col gap-1">
            {features.map((feature) => (
              <li key={feature} className="flex items-start gap-1.5 text-xs leading-4 text-foreground">
                <HugeiconsIcon icon={Tick02Icon} size={12} className="mt-0.5 shrink-0 text-muted-foreground" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Sites · funnels · payment issue · plan details */}
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1.5">
          {showsUsage && sitesLimit > 0 && (
            <span className="text-xs leading-4 text-muted-foreground tabular-nums">
              Sites {sitesUsed} of {hasUnlimitedSites ? 'unlimited' : sitesLimit}
            </span>
          )}
          {isTrialFamily && counts && (
            <span className="text-xs leading-4 text-muted-foreground tabular-nums">{counts}</span>
          )}
          {state === 'past-due' && (
            <span className="text-xs leading-4 text-warning">
              {paymentIssueLabel ?? 'Payment failed. Update your card.'}
            </span>
          )}
          {showsUsage && hasTeamAccess && seatsLimit > 0 && state !== 'past-due' && (
            <span className="text-xs leading-4 text-muted-foreground tabular-nums">
              Seats {seatsUsed} of {seatsLimit >= UNLIMITED_LIMIT ? 'unlimited' : seatsLimit}
            </span>
          )}
          <div className="flex-1" />
          {state === 'paid' && !isScale && (
            <button
              type="button"
              onClick={onUpgrade}
              className="shrink-0 text-xs font-medium leading-4 text-muted-foreground transition-colors hover:text-foreground"
            >
              Compare plans
            </button>
          )}
          {showsPlanDetails && (
            <a
              href="https://humblytics.com/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-xs font-medium leading-4 text-foreground transition-colors hover:text-muted-foreground"
            >
              Plan details
            </a>
          )}
          {/* Ink, not orange: the hub's one orange CTA is Add site. */}
          {ctaLabel && (
            <Button
              size="sm"
              className="h-8 rounded-lg bg-foreground px-3 text-[13px] font-medium text-background hover:bg-foreground/90"
              onClick={ctaAction}
            >
              {ctaLabel}
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}

// ── Shared account-card chrome ─────────────────────────────────────────────────

/** Account-well cards use the artboard's lighter lift (ring + 0 1px 3px), not the
 *  heavier drop `.well` gives a Card by default. */
const ACCOUNT_CARD =
  '!gap-0 !py-0 h-full !rounded-xl !border-transparent !shadow-[0_0_0_1px_rgb(9_9_11/0.07),0_1px_3px_rgb(0_0_0/0.04)] dark:!shadow-[0_0_0_1px_rgb(255_255_255/0.08)]'

function StatusPill({
  children,
  live = false,
  tone,
}: {
  children: React.ReactNode
  live?: boolean
  /** Warning = past due; rust = trial ended (DESIGN.md Urgency Scale). Both carry a dot. */
  tone?: 'warning' | 'rust'
}) {
  return (
    <span
      className={`inline-flex h-5 shrink-0 items-center gap-[5px] rounded-[6px] px-1.5 text-[11.5px] font-medium leading-[14px] tabular-nums ${
        tone === 'warning'
          ? 'bg-warning-tint text-warning'
          : tone === 'rust'
            ? 'bg-urgency-rust-bg text-urgency-rust-fg'
            : 'bg-muted text-foreground'
      }`}
    >
      {live && <span className="size-1.5 shrink-0 rounded-full bg-live" aria-hidden />}
      {tone === 'warning' && <span className="size-1.5 shrink-0 rounded-full bg-warning" aria-hidden />}
      {tone === 'rust' && <span className="size-1.5 shrink-0 rounded-full bg-urgency-rust-accent" aria-hidden />}
      {children}
    </span>
  )
}

// ── MCP Card ───────────────────────────────────────────────────────────────────

/** Same parts as the /api hero (`McpHero`), at card scale. */
export function McpCard({ basePath, isLoading = false }: { basePath: string; isLoading?: boolean }) {
  const navigate = useNavigate()

  return (
    <Card className={ACCOUNT_CARD}>
      <div className="flex h-full gap-5 px-5 py-[18px]">
        <div className="flex min-w-0 flex-1 flex-col gap-3.5">
          <McpEyebrow isLoading={isLoading} />
          <McpHeadline />
          <McpClients />
          <McpConnectButton onClick={() => navigate(`${basePath}/api?setup=mcp`)} className="mt-auto" />
        </div>
        <McpTryAsking limit={2} className="hidden w-[248px] shrink-0 lg:flex" />
      </div>
    </Card>
  )
}
